import {
  HiEye,
  HiDocumentText,
  HiUser,
  HiChevronDown,
  HiChevronUp,
  HiArrowTopRightOnSquare,
  HiLink,
} from 'react-icons/hi2'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { UserAvatar } from '../../../../../components'
import { normalizeObservadorData } from '../../../utils/demandUtils'
import type { Demanda } from '../../../../../models'

interface InfoDemandsProps {
  demanda: Demanda
  viewHelpers?: Record<string, unknown>
}

export const InfoDemands: React.FC<InfoDemandsProps> = ({ demanda }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const navigate = useNavigate()

  const toggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  const handleNavigateToRelacionada = () => {
    if (demanda.relacionada?.id) {
      navigate(`/demands/${demanda.relacionada.id}/view?type=all`)
    }
  }

  // Função para obter resumo das informações
  const getInfoSummary = () => {
    const setor = demanda.setor?.nome_setor || 'Não informado'
    const processo = demanda.processo?.nome_processo || 'Não informado'
    const demandante = demanda.demandante?.name || 'Não informado'

    return `${setor} | ${processo} | ${demandante}`
  }

  return (
    <div className='flex-shrink-0 w-full'>
      {/* Barra de Info Colapsável */}
      <div
        className={`transition-all duration-300 ease-in-out bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl shadow-sm overflow-hidden mb-4 ${
          isExpanded ? 'shadow-md' : 'shadow-sm hover:shadow-md'
        }`}
      >
        {/* Header - Sempre Visível */}
        <button
          data-info-toggle
          onClick={toggleExpand}
          className={`w-full px-5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-neutral-700/50 transition-all duration-300 group ${
            isExpanded ? 'py-3' : 'py-2'
          }`}
        >
          <div className='flex items-center gap-3 flex-1 min-w-0'>
            <div
              className={`transition-all duration-300 ease-in-out ${isExpanded ? 'rotate-0' : 'rotate-0'}`}
            >
              {isExpanded ? (
                <HiChevronUp className='w-5 h-5 text-blue-600 dark:text-blue-400 transition-transform' />
              ) : (
                <HiChevronDown className='w-5 h-5 text-blue-600 dark:text-blue-400 transition-transform' />
              )}
            </div>
            {!isExpanded ? (
              <div className='flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 min-w-0'>
                <span className='font-semibold'>📋</span>
                <span className='truncate'>{getInfoSummary()}</span>
              </div>
            ) : (
              <h3 className='text-base font-semibold text-gray-900 dark:text-gray-100'>
                📋 INFORMAÇÕES COMPLETAS
              </h3>
            )}
          </div>
          <div className='flex items-center gap-3 flex-shrink-0'>
            {/* Informações do Demandante */}
            {demanda.demandante && !isExpanded && (
              <div
                className='flex items-center gap-2 px-2 py-1 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800'
                style={{
                  animation: 'fadeIn 0.2s ease-out',
                }}
              >
                <UserAvatar user={demanda.demandante} size='sm' showName={false} showPopup={true} />
                <div className='hidden lg:flex flex-col min-w-0'>
                  <span className='text-xs font-medium text-green-700 dark:text-green-300 truncate'>
                    {demanda.demandante.name}
                  </span>
                  <span className='text-[10px] text-green-600 dark:text-green-400'>Demandante</span>
                </div>
              </div>
            )}
          </div>
        </button>

        {/* Conteúdo Expansível */}
        {isExpanded && (
          <div
            className='px-5 py-4 border-t border-gray-200 dark:border-neutral-700'
            style={{
              animation: 'slideInFromTop 0.3s ease-out',
            }}
          >
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
              {/* Card: Setor */}
              <div className='group relative bg-white dark:bg-neutral-800 rounded-xl shadow-[4px_4px_10px_rgba(0,0,0,0.08),-4px_-4px_10px_rgba(255,255,255,0.8)] dark:shadow-[4px_4px_10px_rgba(0,0,0,0.3),-2px_-2px_8px_rgba(255,255,255,0.02)] hover:shadow-[6px_6px_16px_rgba(0,0,0,0.1),-6px_-6px_16px_rgba(255,255,255,0.9)] dark:hover:shadow-[6px_6px_16px_rgba(0,0,0,0.4),-3px_-3px_10px_rgba(255,255,255,0.03)] transition-all duration-300 overflow-hidden'>
                {/* Barra lateral colorida */}
                <div className='absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-400 to-blue-600 dark:from-blue-500 dark:to-blue-700'></div>
                <div className='p-4 pl-5'>
                  <div className='flex items-center gap-2 mb-2'>
                    <div className='p-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/30'>
                      <HiDocumentText className='w-4 h-4 text-blue-600 dark:text-blue-400' />
                    </div>
                    <label className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider'>
                      Setor
                    </label>
                  </div>
                  <p className='text-sm text-gray-900 dark:text-gray-100 font-semibold'>
                    {demanda.setor?.nome_setor || 'Não informado'}
                  </p>
                </div>
              </div>

              {/* Card: Processo */}
              <div className='group relative bg-white dark:bg-neutral-800 rounded-xl shadow-[4px_4px_10px_rgba(0,0,0,0.08),-4px_-4px_10px_rgba(255,255,255,0.8)] dark:shadow-[4px_4px_10px_rgba(0,0,0,0.3),-2px_-2px_8px_rgba(255,255,255,0.02)] hover:shadow-[6px_6px_16px_rgba(0,0,0,0.1),-6px_-6px_16px_rgba(255,255,255,0.9)] dark:hover:shadow-[6px_6px_16px_rgba(0,0,0,0.4),-3px_-3px_10px_rgba(255,255,255,0.03)] transition-all duration-300 overflow-hidden'>
                {/* Barra lateral colorida */}
                <div className='absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-400 to-purple-600 dark:from-purple-500 dark:to-purple-700'></div>
                <div className='p-4 pl-5'>
                  <div className='flex items-center gap-2 mb-2'>
                    <div className='p-1.5 rounded-lg bg-purple-50 dark:bg-purple-900/30'>
                      <HiDocumentText className='w-4 h-4 text-purple-600 dark:text-purple-400' />
                    </div>
                    <label className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider'>
                      Processo
                    </label>
                  </div>
                  <p className='text-sm text-gray-900 dark:text-gray-100 font-semibold'>
                    {demanda.processo?.nome_processo || 'Não informado'}
                  </p>
                </div>
              </div>

              {/* Card: Demandante */}
              <div className='group relative bg-white dark:bg-neutral-800 rounded-xl shadow-[4px_4px_10px_rgba(0,0,0,0.08),-4px_-4px_10px_rgba(255,255,255,0.8)] dark:shadow-[4px_4px_10px_rgba(0,0,0,0.3),-2px_-2px_8px_rgba(255,255,255,0.02)] hover:shadow-[6px_6px_16px_rgba(0,0,0,0.1),-6px_-6px_16px_rgba(255,255,255,0.9)] dark:hover:shadow-[6px_6px_16px_rgba(0,0,0,0.4),-3px_-3px_10px_rgba(255,255,255,0.03)] transition-all duration-300 overflow-hidden'>
                {/* Barra lateral colorida */}
                <div className='absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-green-400 to-green-600 dark:from-green-500 dark:to-green-700'></div>
                <div className='p-4 pl-5'>
                  <div className='flex items-center gap-2 mb-2'>
                    <div className='p-1.5 rounded-lg bg-green-50 dark:bg-green-900/30'>
                      <HiUser className='w-4 h-4 text-green-600 dark:text-green-400' />
                    </div>
                    <label className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider'>
                      Demandante
                    </label>
                  </div>
                  {demanda.demandante ? (
                    <div className='flex items-center gap-2'>
                      <UserAvatar
                        user={demanda.demandante}
                        size='sm'
                        showName={false}
                        showPopup={true}
                      />
                      <p className='text-sm text-gray-900 dark:text-gray-100 font-semibold truncate flex-1'>
                        {demanda.demandante.name}
                      </p>
                    </div>
                  ) : (
                    <p className='text-sm text-gray-500 dark:text-gray-400'>Não informado</p>
                  )}
                </div>
              </div>

              {/* Card: Responsáveis */}
              <div className='group relative bg-white dark:bg-neutral-800 rounded-xl shadow-[4px_4px_10px_rgba(0,0,0,0.08),-4px_-4px_10px_rgba(255,255,255,0.8)] dark:shadow-[4px_4px_10px_rgba(0,0,0,0.3),-2px_-2px_8px_rgba(255,255,255,0.02)] hover:shadow-[6px_6px_16px_rgba(0,0,0,0.1),-6px_-6px_16px_rgba(255,255,255,0.9)] dark:hover:shadow-[6px_6px_16px_rgba(0,0,0,0.4),-3px_-3px_10px_rgba(255,255,255,0.03)] transition-all duration-300 overflow-hidden'>
                {/* Barra lateral colorida */}
                <div className='absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-400 to-orange-600 dark:from-orange-500 dark:to-orange-700'></div>
                <div className='p-4 pl-5'>
                  <div className='flex items-center gap-2 mb-2'>
                    <div className='p-1.5 rounded-lg bg-orange-50 dark:bg-orange-900/30'>
                      <HiUser className='w-4 h-4 text-orange-600 dark:text-orange-400' />
                    </div>
                    <label className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider'>
                      {(() => {
                        const count =
                          demanda.demandados && demanda.demandados.length > 0
                            ? demanda.demandados.length
                            : Array.isArray(demanda.demandado)
                              ? demanda.demandado.length
                              : demanda.demandado
                                ? 1
                                : 0
                        return count === 1 ? 'Responsável' : 'Responsáveis'
                      })()}
                    </label>
                  </div>
                  {demanda.demandados && demanda.demandados.length > 0 ? (
                    <div className='flex flex-wrap gap-1'>
                      {demanda.demandados.slice(0, 3).map((user, index) => (
                        <UserAvatar
                          key={index}
                          user={user}
                          size='sm'
                          showName={false}
                          showPopup={true}
                        />
                      ))}
                      {demanda.demandados.length > 3 && (
                        <div className='w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-xs font-medium text-orange-700 dark:text-orange-300 shadow-inner'>
                          +{demanda.demandados.length - 3}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className='text-sm text-gray-500 dark:text-gray-400'>Não atribuído</p>
                  )}
                </div>
              </div>
            </div>

            {/* Segunda linha - Observadores e Demanda Relacionada */}
            {(demanda.observadores && demanda.observadores.length > 0) || demanda.relacionada ? (
              <div className='mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4'>
                {/* Observadores */}
                {demanda.observadores && demanda.observadores.length > 0 && (
                  <div className='group relative bg-white dark:bg-neutral-800 rounded-xl shadow-[4px_4px_10px_rgba(0,0,0,0.08),-4px_-4px_10px_rgba(255,255,255,0.8)] dark:shadow-[4px_4px_10px_rgba(0,0,0,0.3),-2px_-2px_8px_rgba(255,255,255,0.02)] hover:shadow-[6px_6px_16px_rgba(0,0,0,0.1),-6px_-6px_16px_rgba(255,255,255,0.9)] dark:hover:shadow-[6px_6px_16px_rgba(0,0,0,0.4),-3px_-3px_10px_rgba(255,255,255,0.03)] transition-all duration-300 overflow-hidden'>
                    {/* Barra lateral colorida */}
                    <div className='absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-indigo-400 to-indigo-600 dark:from-indigo-500 dark:to-indigo-700'></div>
                    <div className='p-4 pl-5'>
                      <div className='flex items-center gap-2 mb-2'>
                        <div className='p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/30'>
                          <HiEye className='w-4 h-4 text-indigo-600 dark:text-indigo-400' />
                        </div>
                        <label className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider'>
                          Observadores ({demanda.observadores.length})
                        </label>
                      </div>
                      <div className='flex flex-wrap gap-2'>
                        {demanda.observadores
                          .filter(obs => {
                            try {
                              normalizeObservadorData(obs)
                              return true
                            } catch {
                              return false
                            }
                          })
                          .slice(0, 6)
                          .map((obs, index) => {
                            const { user } = normalizeObservadorData(obs)
                            return (
                              <UserAvatar
                                key={index}
                                user={user}
                                size='sm'
                                showName={false}
                                showPopup={true}
                              />
                            )
                          })}
                        {demanda.observadores.length > 6 && (
                          <div className='w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-xs font-medium text-indigo-700 dark:text-indigo-300 shadow-inner'>
                            +{demanda.observadores.length - 6}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Demanda Relacionada */}
                {demanda.relacionada && (
                  <div
                    className='group relative bg-white dark:bg-neutral-800 rounded-xl shadow-[4px_4px_10px_rgba(0,0,0,0.08),-4px_-4px_10px_rgba(255,255,255,0.8)] dark:shadow-[4px_4px_10px_rgba(0,0,0,0.3),-2px_-2px_8px_rgba(255,255,255,0.02)] hover:shadow-[6px_6px_16px_rgba(0,0,0,0.1),-6px_-6px_16px_rgba(255,255,255,0.9)] dark:hover:shadow-[6px_6px_16px_rgba(0,0,0,0.4),-3px_-3px_10px_rgba(255,255,255,0.03)] transition-all duration-300 overflow-hidden cursor-pointer'
                    onClick={handleNavigateToRelacionada}
                  >
                    {/* Barra lateral colorida */}
                    <div className='absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-cyan-400 to-cyan-600 dark:from-cyan-500 dark:to-cyan-700'></div>
                    <div className='p-4 pl-5'>
                      <div className='flex items-center gap-2 mb-3'>
                        <div className='p-1.5 rounded-lg bg-cyan-50 dark:bg-cyan-900/30'>
                          <HiLink className='w-4 h-4 text-cyan-600 dark:text-cyan-400' />
                        </div>
                        <label className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider'>
                          Demanda Relacionada
                        </label>
                        <HiArrowTopRightOnSquare className='w-4 h-4 text-cyan-600 dark:text-cyan-400 ml-auto group-hover:scale-110 transition-transform' />
                      </div>
                      <div className='flex justify-between gap-2'>
                        {/* ID e Título */}
                        <p className='text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2'>
                          #{demanda.relacionada.id} - {demanda.relacionada.titulo}
                        </p>{' '}
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            demanda.relacionada.status === 'FINALIZADA'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : demanda.relacionada.status === 'SOLUCIONADA'
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                : demanda.relacionada.status === 'EMPROGRESSO'
                                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                                  : 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {demanda.relacionada.status}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}
